import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { usePillarsPageData } from '../hooks/usePillarsPageData';
import { useParams } from 'react-router-dom';
import { useFilterContext } from '../context/FilterContext';
import { SectionCard, SectionHeader, SectionLoading, NestedPanel } from './ui/Section';
import { useChartStyles, ChartTooltipCard, readableTextOn } from './ui/chartStyles';

// Fallback color palette for pillars if FilterContext colors aren't available
const FALLBACK_PILLAR_COLORS = [
  '#5E5E5E', // Lifestyle (dark gray)
  '#BF6E50', // Religion (brown-red)
  '#8CB369', // Sports (green)
  '#D96E48', // Institutional (orange)
  '#D98E48', // History (orange-brown)
  '#B58CB3', // DEI (purple)
  '#ECBF68', // Engineering (yellow)
  '#D9A679', // Music (tan)
  '#56A3A6', // Science (teal)
  '#BFB3A8', // Aeronautics (light gray)
  '#47A363', // Innovation (green)
  '#A6923A', // Sustainability (olive)
];

interface PillarEngagementByMonthProps {
  selectedPillars: string[];
  selectedBrands?: string[];
  startDate: string | null;
  endDate: string | null;
}

const PillarEngagementByMonth: React.FC<PillarEngagementByMonthProps> = ({
  selectedPillars,
  selectedBrands = [],
  startDate,
  endDate,
}) => {
  const { orgId: orgIdStr } = useParams<{ orgId: string }>();
  const orgId = parseInt(orgIdStr || '0', 10);
  
  // Get filter context for platforms and pillar colors
  const { selectedPlatforms, pillarColors, pillarColorsLoading } = useFilterContext();
  const cs = useChartStyles();
  const theme = useTheme();
  
  // Function to get pillar color (from FilterContext or fallback)
  const getPillarColor = (pillar: string, index: number) => {
    if (!pillarColorsLoading && pillarColors[pillar]) {
      return pillarColors[pillar];
    }
    return FALLBACK_PILLAR_COLORS[index % FALLBACK_PILLAR_COLORS.length];
  };
  
  // Fetch data using our hook
  const { data, loading, error } = usePillarsPageData({
    orgId,
    pillar: selectedPillars.length === 1 ? selectedPillars[0] : undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined
  });
  
  // Use the selectedBrands prop directly
  const brandsToUse = selectedBrands;
  
  // Process data for the chart - now with percentage calculation
  const chartData = useMemo(() => {
    if (!data) return [];
    
    // Create a map of all months in the data for sorting
    const allMonths = new Set<string>();
    Object.values(data.monthly_pillar_metrics).forEach(pillarData => {
      pillarData.forEach(item => allMonths.add(item.month_year));
    });
    
    // Sort months chronologically
    const sortedMonths = Array.from(allMonths).sort();
    
    // Filter brand pillar metrics if needed
    const filteredBrandPillarMetrics = (() => {
      if (!brandsToUse || brandsToUse.length === 0) {
        return data.brand_pillar_metrics;
      }
      
      // Return only the selected brands
      return Object.entries(data.brand_pillar_metrics)
        .filter(([brand]) => brandsToUse.includes(brand))
        .reduce((acc, [brand, metrics]) => {
          acc[brand] = metrics;
          return acc;
        }, {} as Record<string, any>);
    })();
    
    // Check if we need to filter by brands
    const shouldFilterByBrands = brandsToUse && brandsToUse.length > 0;
    
    // Calculate monthly totals considering brand and platform filters
    const monthlyTotals = sortedMonths.reduce((totals, month) => {
      totals[month] = 0;
      const relevantPillars = selectedPillars.length > 0
        ? data.pillars.filter(p => selectedPillars.includes(p))
        : data.pillars;

      relevantPillars.forEach(pillar => {
        if (shouldFilterByBrands) {
          // Sum from selected brands only
          brandsToUse.forEach(brand => {
            const brandMonthlyDataList = data.brand_pillar_metrics[brand]?.[pillar]?.monthly_metrics;
            const brandMonthlyData = brandMonthlyDataList?.find(m => m.month_year === month);

            if (brandMonthlyData) {
              // Apply platform filtering if needed (based on overall brand/pillar platform data)
              const brandPillarData = data.brand_pillar_metrics[brand]?.[pillar];
              const platformsToFilter = selectedPlatforms && selectedPlatforms.length > 0;
              const hasPlatformData = brandPillarData?.platforms && Object.keys(brandPillarData.platforms).length > 0;

              if (platformsToFilter && hasPlatformData) {
                let platformSumForMonth = 0;
                // IMPORTANT LIMITATION: We filter based on the *overall* platform engagement
                // contribution for the brand/pillar, as monthly platform data isn't available per brand.
                // We assume the monthly total should be filtered if the platform contributed overall.
                // This is an approximation.
                Object.entries(brandPillarData.platforms || {}).forEach(([platform, platformData]: [string, any]) => {
                   if (selectedPlatforms.includes(platform.toLowerCase())) {
                     // If platform matches, include the brand's *entire* monthly sum for this pillar
                     // This is imperfect but the best we can do without monthly platform data per brand.
                     platformSumForMonth += brandMonthlyData.sum_engagement;
                     return; // Add only once per matching platform
                   }
                 });
                 totals[month] += platformSumForMonth > 0 ? brandMonthlyData.sum_engagement : 0;
              } else {
                // No platform filter, add the brand's monthly sum
                totals[month] += brandMonthlyData.sum_engagement;
              }
            }
          });
        } else {
          // Sum from overall monthly metrics
          const pillarMonthData = data.monthly_pillar_metrics[pillar]?.find(item => item.month_year === month);
          if (pillarMonthData) {
            // Apply platform filter if needed (only when not filtering by brand)
            if (selectedPlatforms && selectedPlatforms.length > 0 && pillarMonthData.platforms) {
              let platformSum = 0;
              Object.entries(pillarMonthData.platforms || {}).forEach(([platform, platformData]: [string, any]) => {
                if (selectedPlatforms.includes(platform.toLowerCase())) {
                  platformSum += platformData.sum_engagement;
                }
              });
              totals[month] += platformSum;
            } else {
              totals[month] += pillarMonthData.sum_engagement;
            }
          }
        }
      });
      return totals;
    }, {} as Record<string, number>);
    
    // Prepare data for stacked percentage bar chart
    return sortedMonths.map(month => {
      const monthData: { [key: string]: any } = { 
        month_year: month,
        total: monthlyTotals[month] // Include total for reference
      };
      
      // Filter pillars if selectedPillars is provided
      const relevantPillars = selectedPillars.length > 0
        ? data.pillars.filter(p => selectedPillars.includes(p))
        : data.pillars;
      
      // Add data for each pillar
      relevantPillars.forEach(pillar => {
        let pillarValue = 0;
        
        if (shouldFilterByBrands) {
          // Aggregate from selected brands for this pillar/month
          brandsToUse.forEach(brand => {
            const brandMonthlyDataList = data.brand_pillar_metrics[brand]?.[pillar]?.monthly_metrics;
            const brandMonthlyData = brandMonthlyDataList?.find(m => m.month_year === month);
            
            if (brandMonthlyData) {
              // Apply platform filtering (using same approximation as above)
              const brandPillarData = data.brand_pillar_metrics[brand]?.[pillar];
              const platformsToFilter = selectedPlatforms && selectedPlatforms.length > 0;
              const hasPlatformData = brandPillarData?.platforms && Object.keys(brandPillarData.platforms).length > 0;

              if (platformsToFilter && hasPlatformData) {
                let platformContributed = false;
                Object.entries(brandPillarData.platforms || {}).forEach(([platform, platformData]: [string, any]) => {
                  if (selectedPlatforms.includes(platform.toLowerCase())) {
                    platformContributed = true;
                  }
                });
                if (platformContributed) {
                   pillarValue += brandMonthlyData.sum_engagement;
                }
              } else {
                // No platform filter, add the brand's monthly sum
                pillarValue += brandMonthlyData.sum_engagement;
              }
            }
          });
        } else {
          // Use overall monthly metrics (with platform filtering)
          const pillarMonthData = data.monthly_pillar_metrics[pillar]?.find(item => item.month_year === month);
          if (pillarMonthData) {
            if (selectedPlatforms && selectedPlatforms.length > 0 && pillarMonthData.platforms) {
              Object.entries(pillarMonthData.platforms || {}).forEach(([platform, platformData]: [string, any]) => {
                if (selectedPlatforms.includes(platform.toLowerCase())) {
                  pillarValue += platformData.sum_engagement;
                }
              });
            } else {
              pillarValue = pillarMonthData.sum_engagement;
            }
          }
        }
        
        // Calculate percentage of total
        const totalForMonth = monthlyTotals[month];
        monthData[pillar] = totalForMonth > 0 
          ? (pillarValue / totalForMonth) * 100 
          : 0;
      });
      
      return monthData;
    });
  }, [data, selectedPillars, selectedBrands, selectedPlatforms]);
  
  // Each pillar's share of engagement over the whole period, largest first.
  // Weighted by each month's total so busy months count for more.
  const periodShares = useMemo(() => {
    if (!data || chartData.length === 0) return [] as Array<{ pillar: string; share: number }>;
    const relevantPillars = selectedPillars.length > 0
      ? data.pillars.filter(p => selectedPillars.includes(p))
      : data.pillars;
    const grandTotal = chartData.reduce((sum, m) => sum + (m.total || 0), 0);
    return relevantPillars
      .map(pillar => ({
        pillar,
        share: grandTotal > 0
          ? chartData.reduce((sum, m) => sum + ((m[pillar] || 0) / 100) * (m.total || 0), 0) / grandTotal * 100
          : 0,
      }))
      .sort((x, y) => y.share - x.share);
  }, [data, chartData, selectedPillars]);

  const [hoveredPillar, setHoveredPillar] = useState<string | null>(null);

  // Format X-axis tick labels
  const formatXAxisTick = (value: string) => {
    try {
      // Special case for brand filtered data
      if (value === 'All Time') {
        return 'Brand Totals';
      }
      
      // Check if value is in format "YYYY-MM"
      if (/^\d{4}-\d{2}$/.test(value)) {
        const [year, month] = value.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthIndex = parseInt(month) - 1;
        return `${monthNames[monthIndex]} ${year}`;
      }
      return value;
    } catch (e) {
      return value;
    }
  };
  
  // Custom tooltip formatter to show percentage
  const customTooltipFormatter = (value: number, name: string, props: any) => {
    return [`${value.toFixed(1)}%`, name];
  };

  // Add number formatter helper at the top of the file
  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 1,
      minimumFractionDigits: 1
    }).format(value);
  };
  
  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Sort payload by value in descending order
      const sortedPayload = [...payload].sort((a, b) => b.value - a.value);
      return (
        <ChartTooltipCard
          title={formatXAxisTick(label)}
          rows={sortedPayload.map((entry: any, index: number) => ({
            key: `tooltip-${index}`,
            color: entry.color,
            label: entry.name,
            value: `${formatNumber(entry.value)}%`,
          }))}
        />
      );
    }

    return null;
  };

  const header = (
    <SectionHeader
      title="Engagement by Pillar"
      info="Each month's total engagement split across content pillars and normalized to 100%, so you can compare each pillar's share over time. The list on the left is each pillar's share of engagement for the whole selected period."
    />
  );

  if (loading && !data) {
    return (
      <SectionCard>
        {header}
        <SectionLoading height={360} />
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard>
        {header}
        <Alert severity="error">{error}</Alert>
      </SectionCard>
    );
  }

  if (!data || Object.keys(data.monthly_pillar_metrics).length === 0 || periodShares.length === 0) {
    return (
      <SectionCard>
        {header}
        <Typography color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
          No pillar data for these filters.
        </Typography>
      </SectionCard>
    );
  }

  // Keep each pillar's configured color regardless of the share ordering below
  const colorFor = (pillar: string) => getPillarColor(pillar, Math.max(0, data.pillars.indexOf(pillar)));
  const dimmed = (pillar: string) => hoveredPillar !== null && hoveredPillar !== pillar;

  // Percent label drawn inside a segment when there is room for it
  const renderSegmentLabel = (pillar: string) => (props: any) => {
    const { x, y, width, height, value } = props;
    if (!value || value < 6 || height < 18 || width < 28 || dimmed(pillar)) return null;
    return (
      <text
        x={x + width / 2}
        y={y + height / 2}
        dy="0.35em"
        textAnchor="middle"
        fontSize={11.5}
        fontWeight={600}
        fill={readableTextOn(colorFor(pillar))}
        style={{ pointerEvents: 'none', fontVariantNumeric: 'tabular-nums' }}
      >
        {Math.round(value)}%
      </text>
    );
  };

  const maxShare = Math.max(...periodShares.map((d) => d.share), 1);

  return (
    <SectionCard>
      {header}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '290px minmax(0, 1fr)' }, gap: { xs: 2, lg: 3 } }}>
        {/* Period share, largest first. Doubles as the legend: hover to spotlight a pillar. */}
        <NestedPanel sx={{ py: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.25 }}>
            Share of engagement, whole period
          </Typography>
          <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }} onMouseLeave={() => setHoveredPillar(null)}>
            {periodShares.map(({ pillar, share }) => (
              <Box
                component="li"
                key={pillar}
                onMouseEnter={() => setHoveredPillar(pillar)}
                onFocus={() => setHoveredPillar(pillar)}
                onBlur={() => setHoveredPillar(null)}
                tabIndex={0}
                sx={{
                  py: 0.6,
                  px: 0.75,
                  mx: -0.75,
                  borderRadius: '6px',
                  cursor: 'default',
                  opacity: dimmed(pillar) ? 0.45 : 1,
                  transition: 'opacity 120ms ease',
                  '&:hover, &:focus-visible': { bgcolor: 'action.hover', outline: 'none' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Box component="span" sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: colorFor(pillar), flexShrink: 0, alignSelf: 'center' }} />
                  <Typography variant="body2" component="span" title={pillar} sx={{ flex: 1, minWidth: 0, fontSize: '0.8125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {pillar}
                  </Typography>
                  <Typography variant="body2" component="span" sx={{ fontSize: '0.8125rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {share.toFixed(1)}%
                  </Typography>
                </Box>
                <Box sx={{ mt: 0.5, ml: 2.1, height: 4, borderRadius: 2, bgcolor: 'action.hover', overflow: 'hidden' }}>
                  <Box sx={{ width: `${(share / maxShare) * 100}%`, height: '100%', borderRadius: 2, bgcolor: colorFor(pillar) }} />
                </Box>
              </Box>
            ))}
          </Box>
        </NestedPanel>

        {/* Month by month, 100% stacked, largest pillars at the bottom */}
        <Box sx={{ width: '100%', height: Math.max(380, periodShares.length * 44), minWidth: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
              barCategoryGap={chartData.length <= 4 ? '28%' : '18%'}
              maxBarSize={140}
              onMouseLeave={() => setHoveredPillar(null)}
            >
              <CartesianGrid {...cs.grid} />
              <XAxis
                {...cs.xAxis}
                dataKey="month_year"
                tickFormatter={formatXAxisTick}
                minTickGap={8}
              />
              <YAxis
                {...cs.yAxis}
                width={48}
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
                allowDataOverflow
                tickFormatter={(value) => `${value}%`}
              />
              <RechartsTooltip
                content={CustomTooltip}
                cursor={cs.cursor}
              />
              {periodShares.map(({ pillar }, index) => (
                <Bar
                  key={pillar}
                  dataKey={pillar}
                  stackId="stack"
                  fill={colorFor(pillar)}
                  fillOpacity={dimmed(pillar) ? 0.18 : 1}
                  stroke={theme.palette.background.paper}
                  strokeWidth={1.5}
                  name={pillar}
                  radius={index === periodShares.length - 1 ? [4, 4, 0, 0] : 0}
                  isAnimationActive={false}
                  onMouseEnter={() => setHoveredPillar(pillar)}
                >
                  <LabelList dataKey={pillar} content={renderSegmentLabel(pillar)} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </SectionCard>
  );
};

export default PillarEngagementByMonth; 